export default function DataSourceService({ baseUrl, http, withIosCookies }) {
  const PATH_STORES = '/users/stores';
  const PATH_PROGRESS_TASK_TYPE = '/progress/task_type';
  const PATH_RESOURCES_TASK_TYPE = '/resources/task_types';
  const PATH_RESOURCES_REPORT_OPTIONS = '/resources/report_options';
  const PATH_TASK = '/tasks';
  const PATH_TASKS_STATUS = '/tasks/status';
  const PATH_IMAGES = '/images';
  const PATH_IMAGES_BARCODE = '/images/barcode';
  const PATH_IMAGES_LAST_SEEN = '/images/last_seen';
  const PATH_RESOURCES_AISLES = '/resources/aisles';
  const PATH_NAVIGATION_STATUS = '/nav/status';
  const PATH_PRODUCTS_SEARCH = '/products/search';
  const PATH_PRODUCTS_INFO = '/products/info';
  const PATH_RESOURCES_CATEGORIES = '/resources/categories';
  const PATH_PROGRESS_CATEGORY = '/progress/category';
  const PATH_USERS_INFO = '/users/information';
  const PATH_NEW_PASSWORD = '/users/new_password';
  const PATH_TICKETS = '/tickets';
  const PATH_FEATURE_ACCESS = '/users/feature_access';
  const PATH_BOT_MESSAGES = '/bot/messages';
  const PATH_ALL_BRANDS = '/resources/all_brands';
  const PATH_AISLES_LAYOUT = '/api/layout';
  const PATH_ACTIONS_SLACK = '/actions/slack';
  const PATH_SUPPLIER = '/users/supplier';

  const PATH_VIEWS = '/task_manager/views';
  const PATH_EXCEL = '/resources/excel';

  const PATH_NETWORK_PING = '/network/ping';
  const PATH_NETWORK_METRICS = '/network/metrics';

  const PATH_RESOURCES_OVERHEAD = '/resources/overhead';
  const PATH_RESOURCES_TASK_DETAILS_FEATURES = '/resources/task_details_features';

  return {
    requestViews,
    requestStores,
    requestTask,
    requestProgressTasksType,
    requestResourcesTasksType,
    requestResourcesReportOptions,
    requestTasksStatus,
    requestUpdateTasks,
    requestCreateTasks,
    requestImages,
    requestBarcode,
    requestResourcesAisles,
    requestNavigationStatus,
    requestProductsSearch,
    requestProductsInfo,
    requestCategories,
    requestCategoryProgress,
    requestUserInformation,
    requestUpdateUser,
    requestUpdatePassword,
    requestTickets,
    requestCreateTickets,
    requestBotMessages,
    requestAllBrands,
    requestAislesLayout,
    requestlogErrorSlack,
    requestLastSeen,
    requestSupplier,
    requestPing,
    requestBandwidth,
    saveNetworkMetrics,
    requestExcelDownload,
    requestExcelUpload,
    requestFeatureAccess,
    requestResourcesOverhead,
    requestResourcesTaskDetailsFeatures,
  };

  function requestViews() {
    return http.getAuthenticated({
      url: `${baseUrl}${PATH_VIEWS}`,
      withIosCookies,
    });
  }

  function requestlogErrorSlack(
    slackGroup,
    slackTitle,
    currentUser,
    chain,
    store,
    error,
  ) {
    return http.postAuthenticated({
      url: `${baseUrl}${PATH_ACTIONS_SLACK}`,
      query: {
        channel: slackGroup,
        username: slackTitle,
        text: `[${currentUser}] [${chain}] [${store}] ${error}`,
      },
      withIosCookies,
    });
  }

  function requestAislesLayout({ store }) {
    return http.getAuthenticated({
      url: `${baseUrl}${PATH_AISLES_LAYOUT}`,
      query: { store },
      withIosCookies,
    });
  }

  function requestStores() {
    return http.getAuthenticated({
      url: `${baseUrl}${PATH_STORES}`,
      withIosCookies,
    });
  }

  function requestProgressTasksType(filter, signal) {
    return http.getAuthenticated({
      url: `${baseUrl}${PATH_PROGRESS_TASK_TYPE}`,
      query: filter,
      signal,
      withIosCookies,
    });
  }

  function requestResourcesTasksType({ store }, signal) {
    return http.getAuthenticated({
      url: `${baseUrl}${PATH_RESOURCES_TASK_TYPE}`,
      query: { store },
      signal,
      withIosCookies,
    });
  }

  function requestResourcesReportOptions({ store }, signal) {
    return http.getAuthenticated({
      url: `${baseUrl}${PATH_RESOURCES_REPORT_OPTIONS}`,
      query: { store },
      signal,
      withIosCookies,
    });
  }

  function requestTasksStatus({ store, task_type }, signal) {
    return http.getAuthenticated({
      url: `${baseUrl}${PATH_TASKS_STATUS}`,
      query: { store, task_type },
      signal,
      withIosCookies,
    });
  }

  function requestTask({ parameters, signal }) {
    return http.getAuthenticated({
      url: `${baseUrl}${PATH_TASK}`,
      query: parameters,
      signal,
      withIosCookies,
    });
  }

  function requestUpdateTasks({
    store,
    task_id,
    task_type,
    status,
    real_stock,
    creation_timestamp,
  }) {
    const q = { store, task_id, task_type, status };
    if (real_stock !== undefined && real_stock !== null && real_stock !== '') {
      q.real_stock = real_stock;
    }
    // The API answers 409 when the task was created again after this date.
    if (creation_timestamp) {
      q.creation_timestamp = creation_timestamp;
    }
    return http.patchAuthenticated({
      url: `${baseUrl}${PATH_TASK}`,
      query: q,
      withIosCookies,
    });
  }

  function requestCreateTasks({ parameters }) {
    return http.postAuthenticated({
      url: `${baseUrl}${PATH_TASK}`,
      query: parameters,
      withIosCookies,
    });
  }

  function requestImages({ store, task_id, task_type }, signal) {
    return http.getAuthenticated({
      url: `${baseUrl}${PATH_IMAGES}`,
      query: {
        store,
        task_id,
        task_type,
      },
      signal,
      withIosCookies,
    });
  }

  function requestBarcode({ store, barcode, signal }) {
    return http.getAuthenticated({
      url: `${baseUrl}${PATH_IMAGES_BARCODE}`,
      query: {
        store,
        barcode,
      },
      signal,
      withIosCookies,
    });
  }

  function requestResourcesAisles({ parameters, signal }) {
    return http.getAuthenticated({
      url: `${baseUrl}${PATH_RESOURCES_AISLES}`,
      query: parameters,
      signal,
      withIosCookies,
    });
  }

  function requestResourcesOverhead({ store, task_type, signal }) {
    return http.getAuthenticated({
      url: `${baseUrl}${PATH_RESOURCES_OVERHEAD}`,
      query: { store, task_type },
      signal,
      withIosCookies,
    });
  }

  function requestResourcesTaskDetailsFeatures({ store, task_type, signal }) {
    return http.getAuthenticated({
      url: `${baseUrl}${PATH_RESOURCES_TASK_DETAILS_FEATURES}`,
      query: { store, task_type },
      signal,
      withIosCookies,
    });
  }

  function requestNavigationStatus({ signal, ...parameters }) {
    return http.getAuthenticated({
      url: `${baseUrl}${PATH_NAVIGATION_STATUS}`,
      query: parameters,
      signal,
      withIosCookies,
    });
  }

  function requestProductsSearch({ parameters }) {
    return http.getAuthenticated({
      url: `${baseUrl}${PATH_PRODUCTS_SEARCH}`,
      query: parameters,
      withIosCookies,
    });
  }

  function requestProductsInfo({ store, item, ean, signal }) {
    return http.getAuthenticated({
      url: `${baseUrl}${PATH_PRODUCTS_INFO}`,
      query: { store, item, ean },
      signal,
      withIosCookies,
    });
  }

  function requestCategories({ store, include_subcategories }) {
    return http.getAuthenticated({
      url: `${baseUrl}${PATH_RESOURCES_CATEGORIES}`,
      query: { store, include_subcategories },
      withIosCookies,
    });
  }

  function requestCategoryProgress({ store }) {
    return http.getAuthenticated({
      url: `${baseUrl}${PATH_PROGRESS_CATEGORY}`,
      query: { store },
      withIosCookies,
    });
  }

  function requestUserInformation(headers) {
    return http.getAuthenticated({
      url: `${baseUrl}${PATH_USERS_INFO}`,
      headers,
      withIosCookies,
    });
  }

  function requestUpdateUser({ first_name, last_name }) {
    return http.patchAuthenticated({
      url: `${baseUrl}${PATH_USERS_INFO}`,
      query: { first_name, last_name },
      withIosCookies,
    });
  }

  function requestUpdatePassword({ new_password, confirm_password }) {
    return http.patchAuthenticated({
      url: `${baseUrl}${PATH_NEW_PASSWORD}`,
      query: { new_password, confirm_password },
      withIosCookies,
    });
  }

  function requestTickets() {
    return http.getAuthenticated({
      url: `${baseUrl}${PATH_TICKETS}`,
      withIosCookies,
    });
  }

  function requestCreateTickets({ subject, description, email }) {
    return http.postAuthenticated({
      url: `${baseUrl}${PATH_TICKETS}`,
      query: { subject, description, email },
      withIosCookies,
    });
  }

  function requestBotMessages({ store, languaje }) {
    return http.getAuthenticated({
      url: `${baseUrl}${PATH_BOT_MESSAGES}`,
      query: { store, languaje },
      withIosCookies,
    });
  }

  function requestAllBrands({ store }) {
    return http.getAuthenticated({
      url: `${baseUrl}${PATH_ALL_BRANDS}`,
      query: { store },
      withIosCookies,
    });
  }

  function requestLastSeen({ store, item, ean, signal }) {
    return http.getAuthenticated({
      url: `${baseUrl}${PATH_IMAGES_LAST_SEEN}`,
      query: { store, item, ean },
      signal,
      withIosCookies,
    });
  }

  function requestSupplier({ store }) {
    return http.getAuthenticated({
      url: `${baseUrl}${PATH_SUPPLIER}`,
      query: { store },
      withIosCookies,
    });
  }

  function requestPing() {
    return http.getAuthenticated({
      url: `${baseUrl}${PATH_NETWORK_PING}`,
      withIosCookies,
    });
  }

  function requestBandwidth() {
    return http.postAuthenticated({
      url: `${baseUrl}${PATH_NETWORK_PING}`,
      responseType: 'blob',
      withIosCookies,
    });
  }

  function saveNetworkMetrics(metrics, chain, store, extra_metadata) {
    return http.postAuthenticated({
      url: `${baseUrl}${PATH_NETWORK_METRICS}`,
      body: { ...metrics, chain, store, extra_metadata },
      withIosCookies,
    });
  }

  function requestExcelDownload({ store, task_type, language }) {
    const query = { store, task_type };
    if (language) query.language = language;
    return http.getAuthenticated({
      url: `${baseUrl}${PATH_EXCEL}`,
      query,
      responseType: 'blob',
      withIosCookies,
    });
  }

  function requestFeatureAccess({ store, feature }) {
    return http.getAuthenticated({
      url: `${baseUrl}${PATH_FEATURE_ACCESS}`,
      query: { store, feature },
      withIosCookies,
    });
  }

  function requestExcelUpload({ store, task_type, file, search_excel, dry_run, session }) {
    const formData = new FormData();
    formData.append('file', file);
    const query = { store, task_type };
    if (search_excel) query.search_excel = 'true';
    if (dry_run) query.dry_run = 'true';
    // Session the batch belongs to. Omitted means the backend uses the store's
    // active session; the confirm step sends back the one the dry run reported.
    if (session) query.session = session;
    return http.postAuthenticated({
      url: `${baseUrl}${PATH_EXCEL}`,
      query,
      body: formData,
      withIosCookies,
    });
  }
}
